import * as React from "react";
import { Path } from "react-hook-form";
import {
  useForm,
  FormProvider,
  useFormContext,
  Controller,
  UseFormReturn,
} from "react-hook-form";
import {
  Box,
  Paper,
  Grid,
  Tabs,
  Tab,
  Typography,
  TextField,
  TextFieldProps,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  Autocomplete,
  Modal,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import lombardiaIcon from "../../../assets/icons/Regione Lombardia Loghi.svg";
import "dayjs/locale/it";
import {
  anaggpPacada,
  calProgPacada,
  datiPacada,
  doctor,
  generalResearch,
  ricAnapres,
  ricpgmedt,
  ricGPMetod,
  ricGPDistr,
  ricGPQudia,
  pastma,
} from "../../../networking";
import { Helmet } from "react-helmet-async";
import { AllertMessage } from "../../DoctorsList/Medint/MedintDetails/MedintDetails";
import {
  RHFCheckbox,
  RHFDateTimePicker,
} from "../../RHFComponents/RHFComponents";
import { method } from "lodash";
import { useEffect } from "react";

type Props<T> = {
  name: Path<T>;
  label: string;
  disabled?: boolean;
};

dayjs.locale("it");
export interface PacadaPayload {
  idPacada: string;
  crud: "R" | "U" | "D";
  PACADASWTEMP?: string; // temporaneo
  PACADASWPRIMARIO?: string;
  CODMEDINT?: string; // ? medico ?
  DATAVISITA?: string; // dataVisita
  ORAVISITA?: string; // oraVisita
  DURATA?: string; // durata
  CODSPECIA?: string;
  CODANAPRE?: string;
  CODTPGPRE?: string; // ? tipologia, idTipoPrestazione ?
  CODCLSPRT?: string;
  CODIDTPRE?: string; // ? dataPrenotazione ?
  CODTPRICE?: string;
  IMPEGNATIVA?: string; // impegnativa
  DATAIMPEGNATIVA?: string; // dataImpegnativa
  DATA1DISP?: string; // primaDisponibilita
  IUP?: string; // iup
  CODESENZ?: string;
  CODTIPRAM?: string;
  CODPGMEDT?: string;
  COMPETENZA?: string; // competenza
  DIAGNOSI?: string; // diagnosi
  NOTE?: string; // ? notePrestazione ?
  SWFORZATURA?: string; // forzatura
  SWUSUFRUITO?: string; // usufruito
  SWARRIVATO?: string; // arrivato
  DATAARRIVO?: string; // dataArrivo
  ORAARRIVO?: string; // oraArrivo
  SWASSENZA?: string; // assenza
  MEDSOSTITUITO?: string; // medicoSostituto
  MEDRELAZIONATO?: string; // medRelazionato
  IDACCETTAZIONE?: string; // ? idAccettazione ?
  PRESCRSUGGERITA?: string; // ? prescSuggerita ?
  FLAGALTRO?: string;
  FLAGPIC?: string;
  IDPAI?: string; // idPai
  PROTOCOLLOPIC?: string; // protoPIC
  TOLLGGATTESA?: string; // tolleranzaGiorniAttesa
  APPROFONDIMENTO?: string; // approfondimento
  PRIMOACCESSO?: string; // primoAccesso
  IURP?: string;
  CODANAPRES?: string;
  IDAPPGP?: string;
  CODANAGGP?: string;
  IDPROGCOMBINATO?: string;
  CODISTCOM?: string;
  CODGPMETOD?: string;
  CODGPQUDIA?: string;
  CODGPDISTR?: string;
  CODNOMENCLREG?: string;
  CODCATALOGOPRESCR?: string;
  PRESCRITTORE?: string;
  CODOPERATORE?: string;
  aziendaPrenotazione?: string;
  DESANAGGP?: string;
  DESANAPRES?: string;
  DESCLSPRT?: string;
  DESESENZ?: string;
  DESGPDISTR?: string;
  DESGPMETOD?: string;
  DESGPQUDIA?: string;
  DESIDTPRE?: string;
  DESISTCOM?: string;
  DESTIPRAM?: string;
  DESTPRICE?: string;
  DISCIPLINAANAGGP?: string;
  NOMINMEDICOREL?: string;
  NOMINMEDICOSOST?: string;
  FLAGPREZZO?: string;
  OGGSPERIMENTAZIONE?: string;
}

// Tipo dei dati del form
type FormValues = {
  // top
  dataPrenotazione: Dayjs | null; // ??? CODIDTPRE ???
  numeroPrenotazione: string; // ???
  paziente: string; // ???
  medico: string; // ? CODMEDINT ?

  dataVisita: Dayjs | null; // DATAVISITA
  oraVisita: Dayjs | null; // ORAVISITA
  durata: string; // DURATA
  operatore: string; // CODOPERATORE

  // status flags
  forzatura: boolean; // SWFORZATURA
  usufruito: boolean; // SWUSUFRUITO
  inArrivo: boolean; // ???
  arrivato: boolean; // SWARRIVATO
  daPagare: boolean; // ???
  assenza: boolean; // SWASSENZA
  sospeso: boolean; // ???

  dataAccettazione: Dayjs | null; // ??? è presente IDACCETTAZIONE non data ???
  numeroAccettazione: string; // IDACCETTAZIONE

  dataArrivo: Dayjs | null; // DATAARRIVO
  oraArrivo: Dayjs | null; // ORAARRIVO
  tolleranzaGiorniAttesa: boolean; // TOLLGGATTESA
  approfondimento: boolean; // APPROFONDIMENTO
  suggerita: boolean; // ? PRESCRSUGGERITA ?

  primaDisponibilita: Dayjs | null; // DATA1DISP
  idPAI: string; // IDPAI
  cronico: boolean; // ???
  primoAccesso: boolean; // PRIMOACCESSO

  competenza: string; // COMPETENZA
  protPIC: string; // PROTOCOLLOPIC
  tipologia: string; // ? CODTPGPRE ?

  impegnativa: string; // IMPEGNATIVA
  dataImpegnativa: Dayjs | null; // DATAIMPEGNATIVA

  iup: string; // IUP
  classePriorita: string; // ???

  diagnosi: string; // DIAGNOSI
  notePrestazione: string; // ? NOTE ?

  idTipoPrestazione: string; // ? CODTPGPRE ?
  medicoRelaz: string; // MEDRELAZIONATO
  medicoSostituto: string; // MEDSOSTITUITO
  temporaneo: boolean; // PACADASWTEMP

  modaleStampaAgenda: {
    dallaData?: Dayjs | null;
    allaData?: Dayjs | null;
    rifTelefono?: boolean;
  };

  //Dati GP
  idAppuntamento?: string; // IDAPPGP
  idCombinato?: string; // IDPROGCOMBINATO
  codiceIURP?: string; // IURP
  prestazioneSISS?: string; // CODCATALOGOPRESCR / (se diverso, mappalo)
  codAgenda?: string; // CODANAGGP
  idCombinata?: string; // CODISTCOM
  quadroDiagnostico?: string; // CODGPQUDIA
  metodica?: string; // CODGPMETOD
  distretto?: string; // CODGPDISTR (o CODISTCOM se diverso)
  esenzione?: string; // CODESENZ
  prescrittore?: string; // PRESCRITTORE
  provenienza?: string; // CODNOMENCLREG (se inteso come provenienza, altrimenti mappa corretta)
  tipoRicetta?: string; // CODTPRICE
  programma?: string; // CODGPDISTR / altro codice programma
  nomenclatoreReg?: string; // CODNOMENCLREG
  catalogoPrescr?: string; // CODCATALOGOPRESCR
};

// Carica le opzioni via generalResearch
// --- in alto al file (fuori dal componente) ---
const __optsCache: Map<string, Option[]> = new Map();

// Piccolo helper: TextField collegato a RHF (per input normali)
export const RHFText = (props: TextFieldProps & { name: keyof FormValues }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<FormValues>();
  const err = errors[props.name]?.message as string | undefined;
  return (
    <TextField
      fullWidth
      InputLabelProps={{ shrink: true }}
      size="small"
      placeholder="Placeholder"
      {...props}
      {...register(props.name)}
      error={!!err}
      helperText={err ? err : props.helperText}
    />
  );
};

export const RHFSelect = (
  props: TextFieldProps & {
    name: keyof FormValues;
    options: { label: string; value: string }[];
  }
) => {
  const { register } = useFormContext<FormValues>();
  return (
    <TextField
      select
      fullWidth
      InputLabelProps={{ shrink: true }}
      size="small"
      placeholder="Placeholder"
      {...props}
      {...register(props.name)}
    >
      {props.options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

// Campo data
export const RHFDate = ({
  name,
  label,
}: {
  name: keyof FormValues;
  label?: string;
}) => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field }) => {
        const value = dayjs(field.value).isValid()
          ? dayjs(field.value).format("YYYY-MM-DD")
          : "";

        return (
          <TextField
            label={label}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={value}
            onChange={(e) =>
              field.onChange(e.target.value ? dayjs(e.target.value) : null)
            }
            fullWidth
            size="small"
          />
        );
      }}
    />
  );
};

// Campo ora
export const RHFTime = ({
  name,
  label,
}: {
  name: keyof FormValues;
  label?: string;
}) => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field }) => (
        <TimePicker
          label={label}
          value={field.value as Dayjs | null}
          format={"HH:mm"}
          ampm={false}
          onChange={field.onChange}
          slotProps={{
            textField: { size: "small", fullWidth: true, placeholder: "01:00" },
          }}
        />
      )}
    />
  );
};

// Checkbox collegato a RHF
export const RHFCheck = ({
  name,
  label,
  disabled,
}: {
  name: keyof FormValues;
  label: string;
  disabled?: boolean;
}) => {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field }) => (
        <FormControlLabel
          label={label}
          control={
            <Checkbox
              checked={!!field.value}
              disabled={disabled}
              onChange={(_, v) => field.onChange(v)}
              size="small"
              sx={{ p: 0.5 }}
            />
          }
          sx={{
            m: 0,
            height: 40,
            alignItems: "center",
            "& .MuiFormControlLabel-label": {
              lineHeight: "20px",
              fontSize: 14,
            },
          }}
        />
      )}
    />
  );
};

// Autocomplete RHF: salva nel form il VALUE (stringa). Supporta anche freeSolo.

type Option = { label: string; value: string };

type RHFAutocompleteProps = {
  name: keyof FormValues;
  label: string;
  options: Option[] | string[] | string;
  freeSolo?: boolean;
  readOnly?: boolean;
};

export const RHFAutocomplete = ({
  name,
  label,
  options,
  freeSolo = false,
  readOnly,
}: RHFAutocompleteProps) => {
  const { control } = useFormContext<FormValues>();

  const normalizedOptions: Option[] = React.useMemo(() => {
    if (typeof options === "string") {
      // Caso singola stringa
      return [{ label: options, value: options }];
    } else if (typeof options[0] === "string") {
      // Caso array di stringhe
      return (options as string[]).map((s) => ({ label: s, value: s }));
    }
    // Caso array di oggetti
    return options as Option[];
  }, [options]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const currentOption =
          normalizedOptions.find((o) => o.value === (field.value ?? "")) ??
          null;

        return (
          <Autocomplete
            renderOption={(props, option) => (
              <li {...props} key={`opt_${(option as any).value}`}>
                {(option as any).label}
              </li>
            )}
            options={normalizedOptions}
            value={currentOption}
            freeSolo={freeSolo}
            onChange={(_, val) => {
              if (typeof val === "string") return field.onChange(val);
              field.onChange(val?.value ?? "");
            }}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        );
      }}
    />
  );
};

export const TabPanel = (props: {
  value: number;
  index: number;
  children: React.ReactNode;
}) => {
  const { value, index, children } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ opacity: 0.7, mb: 1 }}>
    {children}
  </Typography>
);

type AppointmentListDetailProps = {
  onClose: () => void;
  dataAppuntamento: any;
  azienda: string | undefined;
  prenotazioneRequest: (
    numeroPrenotazione: string,
    aziendaPrenotazione: string
  ) => Promise<void>;
};

// Modale della Stampa Agenda
export const StampaAgendaModale = (props: {
  methods: UseFormReturn<any>;
  showModaleStampaAgenda: boolean;
  setShowModaleStampaAgenda: (value: boolean) => void;
}) => {
  // modo più rapido per gestire la definizione al rendering della componenente del tipo necessario dayjs
  // poiché a monte viene passato un generico any che causa problemi, sarebbe necessario gestire la cosa a livello delle componenti rhf
  useEffect(() => {
    if (props.showModaleStampaAgenda) {
      props.methods.setValue("modaleStampaAgenda.dallaData", dayjs());
      props.methods.setValue("modaleStampaAgenda.allaData", dayjs());
      props.methods.setValue("modaleStampaAgenda.rifTelefono", dayjs());
    }
  }, [props.showModaleStampaAgenda]);

  // Gestisce il cambio della data "alla" in caso l'utente selezioni una data "dalla" superiore
  useEffect(() => {
    const tempDalla = props.methods.getValues("modaleStampaAgenda.dallaData");
    const tempAlla = props.methods.getValues("modaleStampaAgenda.allaData");

    if (tempDalla >= tempAlla) {
      props.methods.setValue("modaleStampaAgenda.allaData", tempDalla);
    }
  }, [props.methods.watch("modaleStampaAgenda.dallaData")]);

  // Metodo richiamato dall'evento al click del pulsante pdf per la stampa del documento
  const assignedAgendaToPrint = async () => {
    const tempMedico = props.methods.getValues("medIntDettaglio.codice");
    const tempDataDalla = props.methods.getValues(
      "modaleStampaAgenda.dallaData"
    );
    const tempDataAlla = props.methods.getValues("modaleStampaAgenda.allaData");
    const tempRiferimentoTelefonico = props.methods.getValues("modaleStampaAgenda.rifTelefono");    

    try {
      const response = await pastma({
        aziendaPrenotazione: "MEDITEL",
        dataInizioElaborazione: dayjs(tempDataDalla).format("YYYY-MM-DD"),
        dataFineElaborazione: dayjs(tempDataAlla).format("YYYY-MM-DD"),
        oraInizioElaborazione: "0",
        oraFineElaborazione: "2359",
        stringaGiorni: "SSSSSSS",
        medicoInizioElaborazione: tempMedico,
        medicoFineElaborazione: tempMedico,
        riferimentoTelefonico: tempRiferimentoTelefonico ? "S" : "",
        riferimentoRegime: "",
        mailMedici: "",
        raggrInizioElaborazione: "",
        raggrFineElaborazione: "",
        ordineStampa: "0",
        tipoStampa: "0",
      });

      const esitoObj = response.data.dati.find(
        (item: any) => item?.campo === "esito"
      );

      const esitoResponse = Number(esitoObj?.dato) === 0;

      const messaggioObj = response.data.dati.find(
        (item: any) => item && typeof item === "object" && "campo" in item
      );

      const pdfObj = response.data.dati.find(
        (item: any) => item && typeof item === "object" && "pdfB64" in item
      );

      const pdfB64 = pdfObj?.pdfB64;

      if (!esitoResponse || !pdfObj) {
        throw new Error("Errore nella generazione del PDF");
      }

      // Decodifica il Base64 in un array di byte
      const byteCharacters = atob(pdfB64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Crea un Blob con il tipo MIME PDF
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Crea un link per il download
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `agenda_${tempMedico}_${tempDataDalla}_${tempDataAlla}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Pulizia dell'elemento
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Errore nel download del PDF:", error);
    }
  };

  return (
    <Modal open={props.showModaleStampaAgenda}>
      <FormProvider {...props.methods}>
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "150vh",
            width: "50%",
            overflow: "auto",
            paddingTop: 1,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Tabs value={0} sx={{ paddingLeft: 2 }}>
            <Tab
              sx={{
                fontFamily: "Montserrat",
                fontWeight: "bold",
                textTransform: "none",
              }}
              label="Stampa Agenda Medico"
            ></Tab>
          </Tabs>
          <Grid container spacing={2} padding={2}>
            <Grid item xs={12} md={12}>
              <RHFDateTimePicker
                label="Dalla Data"
                dateOnly
                fullWidth
                name="modaleStampaAgenda.dallaData"
                serialize="dayjs"
                control={props.methods.control}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <RHFDateTimePicker
                label="Alla Data"
                dateOnly
                fullWidth
                minValue={props.methods?.getValues(
                  "modaleStampaAgenda.dallaData"
                )}
                name="modaleStampaAgenda.allaData"
                serialize="dayjs"
                control={props.methods.control}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Box paddingLeft={2}>
                <RHFCheckbox
                  label="Riferimento Telefonico"
                  name="modaleStampaAgenda.rifTelefono"
                  control={props.methods.control}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={12} width={"100%"}>
              <Grid container display={"flex"} justifyContent={"space-between"}>
                <Grid item>
                  <Button
                    variant="outlinedError"
                    onClick={() => props.setShowModaleStampaAgenda(false)}
                  >
                    Uscita
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="containedCustom"
                    onClick={() => {
                      assignedAgendaToPrint();
                    }}
                  >
                    Conferma Stampa Agenda
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </Modal>
  );
};

export default function AppointmentListDetail({
  onClose,
  dataAppuntamento,
  azienda,
  prenotazioneRequest,
}: AppointmentListDetailProps) {
  const [tab, setTab] = React.useState(0);
  const [showModaleStampaAgenda, setShowModaleStampaAgenda] =
    React.useState(false);

  // Allert di conferma/risultato
  const [showAllert, setShowAllert] = React.useState(false);
  const [severity, setSeverity] = React.useState<
    "warning" | "success" | "error"
  >("warning");
  const [message, setMessage] = React.useState("");

  // Chiedo conferma prima di inviare la modifica
  const askConfirmEditPrenotazione = () => {
    methods.handleSubmit(() => {
      setMessage("Confermare l'aggiornamento dell'appuntamento?");
      setSeverity("warning");
      setShowAllert(true);
    })();
  };

  // Eseguo l’update quando l’utente conferma nell’alert
  const onAlertConfirm = async () => {
    try {
      await methods.handleSubmit(sendAppointmentDetailsUpdate)();
    } catch (err) {
      console.error(err);
      setSeverity("error");
      setMessage("Si è verificato un errore durante la modifica.");
    }
  };

  // L'alert si chiude dopo 4 secondi se non è di tipo warning
  React.useEffect(() => {
    if (showAllert && severity !== "warning") {
      const t1 = setTimeout(() => setShowAllert(false), 4000);
      const t2 = setTimeout(() => {
        setSeverity("warning");
        setMessage("");
      }, 6000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [showAllert, severity]);

  const fmtDate = (d: any, fallback = "1901-01-01") => {
    const v = dayjs(d);
    return v.isValid() ? v.format("YYYY-MM-DD") : fallback;
  };

  const fmtTime = (t: any, fallback = "0") => {
    // TimePicker ti passa un Dayjs o null
    if (dayjs.isDayjs(t) && t.isValid()) return t.format("HHmm");
    // se arrivasse una stringa tipo "1730" o "17:30"
    const s = typeof t === "string" ? t.trim() : "";
    if (s) {
      const tryA = dayjs(s, "HH:mm", true);
      if (tryA.isValid()) return tryA.format("HHmm");
      const tryB = dayjs(s, "HHmm", true);
      if (tryB.isValid()) return tryB.format("HHmm");
    }
    return fallback;
  };

  //Nel caso in cui l'ora sia salvata come "0" o "0000" la converte in null
  const parseHHmmToDayjs = (v: any): Dayjs | null => {
    const s = String(v ?? "").trim();
    if (!s || s === "0" || s === "0000") return null;

    const d = dayjs(s, "HHmm", true);
    return d.isValid() ? d : null;
  };

  // Valori di default del form
  const defaultValues: FormValues = {
    dataPrenotazione: dayjs(dataAppuntamento?.DATAPRENOTAZIONE),
    numeroPrenotazione: dataAppuntamento?.NUMPRENOTAZIONE,
    paziente: dataAppuntamento?.NOMINPAZIENTE,
    medico: dataAppuntamento?.CODMEDINT ?? "",

    dataVisita: dayjs(dataAppuntamento?.DATAVISITA),
    oraVisita: dayjs(dataAppuntamento?.ORAVISITA, "HHmm"),
    durata: dataAppuntamento?.DURATA,
    operatore: dataAppuntamento?.CODOPERATORE,

    forzatura: dataAppuntamento?.SWFORZATURA ? false : true,
    usufruito: dataAppuntamento?.SWUSUFRUITO ? true : false,
    inArrivo: dataAppuntamento?.SWARRIVATO === 0 ? true : false,
    arrivato: dataAppuntamento?.SWARRIVATO === 0 ? true : false,
    daPagare: false,
    assenza: dataAppuntamento?.SWASSENZA ? true : false,
    sospeso: false,

    dataAccettazione: dayjs(dataAppuntamento?.DATAACCETTAZIONE),
    numeroAccettazione: dataAppuntamento?.PROGACCETTAZIONE,

    dataArrivo: dayjs(dataAppuntamento?.DATAARRIVO),
    oraArrivo: parseHHmmToDayjs(dataAppuntamento?.ORAARRIVO),
    tolleranzaGiorniAttesa:
      dataAppuntamento?.TOLLGGATTESA === "" ? false : true,
    approfondimento: dataAppuntamento?.APPROFONDIMENTO === "" ? false : true,
    suggerita: dataAppuntamento?.PRESCRSUGGERITA === "" ? false : true,

    primaDisponibilita: dayjs(dataAppuntamento?.DATA1DISP),
    idPAI: dataAppuntamento?.IDPAI === "" ? "" : dataAppuntamento?.IDPAI,
    cronico: dataAppuntamento?.FLAGPIC === "" ? false : true,
    primoAccesso: dataAppuntamento?.PRIMOACCESSO === "" ? false : true,

    competenza: dataAppuntamento?.COMPETENZA,
    protPIC:
      dataAppuntamento?.PROTOCOLLOPIC === ""
        ? ""
        : dataAppuntamento?.PROTOCOLLOPIC,
    tipologia: dataAppuntamento?.CODTPGPRE,

    impegnativa:
      dataAppuntamento?.IMPEGNATIVA === "" ? "" : dataAppuntamento?.IMPEGNATIVA,
    dataImpegnativa: dayjs(dataAppuntamento?.DATAIMPEGNATIVA),

    iup: dataAppuntamento?.IUP === "" ? "" : dataAppuntamento?.IUP,
    classePriorita:
      dataAppuntamento?.CODCLSPRT === "" ? "" : dataAppuntamento?.CODCLSPRT,

    diagnosi: dataAppuntamento?.DIAGNOSI,
    notePrestazione: dataAppuntamento?.NOTE,

    idTipoPrestazione: dataAppuntamento?.CODIDTPRE ?? "",
    medicoRelaz: dataAppuntamento?.NOMINMEDICOREL ?? "",
    medicoSostituto: dataAppuntamento?.NOMINMEDICOSOST ?? "",
    temporaneo: dataAppuntamento?.PACADASWTEMP === "T" ? true : false,

    modaleStampaAgenda: {
      dallaData: dayjs(),
      allaData: dayjs(),
      rifTelefono: false,
    },

    idAppuntamento: String(dataAppuntamento?.IDAPPGP || ""),
    idCombinato: String(dataAppuntamento?.IDPROGCOMBINATO || ""),
    codiceIURP: String(dataAppuntamento?.IURP || ""),
    prestazioneSISS: String(dataAppuntamento?.CODANAPRES || ""),
    codAgenda: String(dataAppuntamento?.CODANAGGP || ""),
    idCombinata: String(dataAppuntamento?.CODISTCOM || ""),
    quadroDiagnostico: String(dataAppuntamento?.CODGPQUDIA || ""),
    metodica: String(dataAppuntamento?.CODGPMETOD || ""),
    distretto: String(dataAppuntamento?.CODGPDISTR || ""),
    esenzione: String(dataAppuntamento?.CODESENZ || ""),
    prescrittore: String(dataAppuntamento?.PRESCRITTORE || ""),
    provenienza: String(dataAppuntamento?.CODTIPRAM || ""),
    tipoRicetta: String(dataAppuntamento?.CODTPRICE || ""),
    programma: String(dataAppuntamento?.CODPGMEDT || ""),
    nomenclatoreReg: String(dataAppuntamento?.CODNOMENCLREG || ""),
    catalogoPrescr: String(dataAppuntamento?.CODCATALOGOPRESCR || ""),
  };

  const methods = useForm<FormValues>({ defaultValues });

  // funzione send del payload di eventuali modifiche apportate sul form
  const sendAppointmentDetailsUpdate = async () => {
    try {
      const payload: PacadaPayload = {
        idPacada: dataAppuntamento.idPacada,
        crud: "U",
        PACADASWTEMP: methods.getValues("temporaneo") === true ? "T" : "D", //corretto
        PACADASWPRIMARIO: "S",
        CODMEDINT: methods.getValues("medico") || "",
        DATAVISITA:
          dayjs(methods.getValues("dataVisita")).format("YYYY-MM-DD") ||
          "0001-01-01",
        ORAVISITA: dayjs(methods.getValues("oraVisita")).format("HHmm") || "0",
        DURATA: methods.getValues("durata") || "0",
        CODSPECIA: dataAppuntamento.CODSPECIA || "",
        CODANAPRE: dataAppuntamento.CODANAPRE || "",
        CODTPGPRE: methods.getValues("idTipoPrestazione") || "",
        CODCLSPRT:
          methods.getValues("classePriorita") === ""
            ? ""
            : methods.getValues("classePriorita"),
        CODIDTPRE: dataAppuntamento.CODIDTPRE,
        IMPEGNATIVA:
          methods.getValues("impegnativa") === ""
            ? ""
            : methods.getValues("impegnativa"),
        DATAIMPEGNATIVA:
          dayjs(methods.getValues("dataImpegnativa")).format("YYYY-MM-DD") ||
          "0001-01-01",
        DATA1DISP:
          dayjs(methods.getValues("primaDisponibilita")).format("YYYY-MM-DD") ||
          "0001-01-01",
        IUP: methods.getValues("iup") === "" ? "" : methods.getValues("iup"),
        CODESENZ: methods.getValues("esenzione") || "",
        COMPETENZA: methods.getValues("competenza") || "0",
        DIAGNOSI: methods.getValues("diagnosi") || "",
        NOTE: methods.getValues("notePrestazione") || "",
        SWFORZATURA: methods.getValues("forzatura") ? "1" : "0",
        SWUSUFRUITO: methods.getValues("usufruito") ? "S" : "",
        SWARRIVATO: methods.getValues("arrivato") ? "S" : "",
        DATAARRIVO: fmtDate(methods.getValues("dataArrivo")),
        ORAARRIVO: fmtTime(methods.getValues("oraArrivo")),
        SWASSENZA: methods.getValues("assenza") ? "s" : "",
        MEDSOSTITUITO:
          methods.getValues("medicoSostituto") === ""
            ? ""
            : methods.getValues("medicoSostituto"),
        MEDRELAZIONATO:
          methods.getValues("medicoRelaz") === ""
            ? ""
            : methods.getValues("medicoRelaz"),
        IDACCETTAZIONE: methods.getValues("numeroAccettazione") || "0",
        PRESCRSUGGERITA: methods.getValues("suggerita") ? "" : "S",
        FLAGALTRO: "", //obbligatorio
        FLAGPIC: methods.getValues("cronico") ? "1" : "0",
        IDPAI:
          methods.getValues("idPAI") === "" ? "" : methods.getValues("idPAI"),
        PROTOCOLLOPIC:
          methods.getValues("protPIC") === ""
            ? ""
            : methods.getValues("protPIC"),
        TOLLGGATTESA: methods.getValues("tolleranzaGiorniAttesa") ? "1" : "0",
        APPROFONDIMENTO: methods.getValues("approfondimento") ? "1" : "0",
        PRIMOACCESSO: methods.getValues("primoAccesso") ? "1" : "0",
        IURP: methods.getValues("codiceIURP") || "",
        CODANAPRES: methods.getValues("prestazioneSISS") || "",
        IDAPPGP: methods.getValues("idAppuntamento") || "",
        CODANAGGP: methods.getValues("codAgenda") || "",
        IDPROGCOMBINATO: methods.getValues("idCombinato") || "",
        CODISTCOM: methods.getValues("idCombinata") || "",
        CODGPMETOD: methods.getValues("metodica") || "",
        CODGPQUDIA: methods.getValues("quadroDiagnostico") || "",
        CODGPDISTR: methods.getValues("distretto") || "",
        CODNOMENCLREG: methods.getValues("nomenclatoreReg") || "",
        CODCATALOGOPRESCR: methods.getValues("catalogoPrescr") || "",
        PRESCRITTORE: methods.getValues("prescrittore") || "",
        CODTPRICE: methods.getValues("tipoRicetta") || "",
        CODTIPRAM: methods.getValues("provenienza") || "",
        CODPGMEDT: methods.getValues("programma") || "",
        CODOPERATORE: methods.getValues("operatore") || "",
        OGGSPERIMENTAZIONE: dataAppuntamento.OGGSPERIMENTAZIONE ?? "",
        FLAGPREZZO: dataAppuntamento.FLAGPREZZO ?? "",
      };

      const response = await datiPacada(payload);
      console.log("Risposta dal server:", response);
      if (response.data.esito === "OK") {
        setSeverity("success");
        setMessage(response.data.messaggio);
      }
    } catch (err) {
      console.error("Errore fetch datiPacada:", err);
    }
  };

  const TabPanel = (props: {
    value: number;
    index: number;
    children: React.ReactNode;
  }) => {
    const { value, index, children } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tab-panel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
      </div>
    );
  };

  const resetAccettazione = () => {
    methods.resetField("numeroAccettazione");
    methods.resetField("dataAccettazione");
  };

  //Funzione per generare le options degli AutoComplete di Dati GP se esiste il codice
  const filterOptions = (code?: string, desc?: string) =>
    code
      ? [
          {
            value: String(code),
            label: desc ? `${code} - ${desc}` : String(code),
          },
        ]
      : [];

  /*Converte un record api doctor in una Option per l’autocomplete.
    Il value è il codice medico (quello che salviamo nel form)
    Il label è quello che mostriamo all’utente (codice + descrizione)*/
  const mapDoctor = (d: any): Option => ({
    value: String(d.CODICE),
    label: d.DESCRIZIONE ? `${d.CODICE} - ${d.DESCRIZIONE}` : String(d.CODICE),
  });

  // Stato options Medici: pre-popolato col medico corrente
  const [mediciOpts, setMediciOpts] = React.useState<Option[]>(() => {
    const code = String(dataAppuntamento?.CODMEDINT || "");
    const name = dataAppuntamento?.NOMINMEDICO || "";
    return code
      ? [{ value: code, label: name ? `${code} - ${name}` : code }]
      : [];
  });

  /* Al mount:
 1) chiamo l’api per prendere la lista completa dei medici
 2) la trasformo in Option[]
 3) mi assicuro che il medico attuale sia presente in cima ed evito duplicati
 */
  React.useEffect(() => {
    //Variabile per evitare di fare setState durante l'unmount del componente
    let active = true;
    (async () => {
      try {
        const res = await doctor({
          tipoAgenda: "",
          listino: "",
          specialita: "",
          prestazione: "",
          medico: "",
        });

        const list = (res?.data?.dati ?? []).map(mapDoctor);

        // aggiungo il medico corrente se non è presente
        const currentCode = String(dataAppuntamento.CODMEDINT || "");
        const currentLabel =
          dataAppuntamento.NOMINMEDICO ||
          list.find((l: Option) => l.value === currentCode)?.label ||
          currentCode;

        // Metto il medico attuale in testa e il resto della lista, eliminando doppioni
        const merged = currentCode
          ? [{ value: currentCode, label: currentLabel }, ...list].filter(
              (opt, i, arr) => arr.findIndex((o) => o.value === opt.value) === i
            )
          : list;

        if (active) setMediciOpts(merged);
      } catch (e) {
        console.error("doctor() error:", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Mappa un record dell’API in Option
  // Accetta sia {CODICE, DESCRIZIONE} che {codice, descrizione}.
  const mapApiItemToOption = (r: any): Option => {
    const code = String(r.CODICE ?? r.codice ?? r.pkey ?? "");
    const desc = r.DESCRIZIONE ?? r.descrizione ?? "";
    return {
      value: code,
      label: desc ? `${code} - ${desc}` : code,
    };
  };

  const useResearchOptions = (
    tabella: "ISTCOM" | "ESENZ" | "TIPRAM" | "TPRICE",
    initialOption?: Option,
    customLoader?: () => Promise<Option[]>, // NEW: loader alternativo
    cacheKey?: string // NEW: chiave cache
  ) => {
    const key = cacheKey || tabella;

    const [options, setOptions] = React.useState<Option[]>(
      initialOption ? [initialOption] : []
    );
    const [loading, setLoading] = React.useState(false);
    const [loadedOnce, setLoadedOnce] = React.useState<boolean>(() =>
      __optsCache.has(key)
    );
    const abortRef = React.useRef<AbortController | null>(null);

    React.useEffect(() => {
      if (__optsCache.has(key)) {
        const cached = __optsCache.get(key)!;
        setOptions(
          initialOption
            ? [initialOption, ...cached].filter(
                (o, i, a) => a.findIndex((x) => x.value === o.value) === i
              )
            : cached
        );
        setLoadedOnce(true);
      } else {
        setOptions(initialOption ? [initialOption] : []);
        setLoadedOnce(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    const load = React.useCallback(async () => {
      if (loadedOnce) return;
      if (__optsCache.has(key)) {
        const cached = __optsCache.get(key)!;
        setOptions(
          initialOption
            ? [initialOption, ...cached].filter(
                (o, i, a) => a.findIndex((x) => x.value === o.value) === i
              )
            : cached
        );
        setLoadedOnce(true);
        return;
      }

      setLoading(true);
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      try {
        let list: Option[];
        if (customLoader) {
          list = await customLoader();
        } else {
          const payload = {
            tabella,
            codice: "",
            filtro: "",
            applicazione: "",
            attivi: "0",
          };
          const res = await generalResearch(payload as any, ac.signal);
          const raw = res?.data?.dati ?? res?.data ?? [];
          list = (Array.isArray(raw) ? raw : Object.values(raw)).map(
            mapApiItemToOption
          );
        }

        __optsCache.set(key, list);

        setOptions(
          initialOption
            ? [initialOption, ...list].filter(
                (o, i, a) => a.findIndex((x) => x.value === o.value) === i
              )
            : list
        );
        setLoadedOnce(true);
      } catch (e: any) {
        if (e?.name !== "CanceledError")
          console.error("options loader error:", e);
      } finally {
        setLoading(false);
      }
    }, [key, loadedOnce, customLoader, tabella, initialOption]);

    React.useEffect(() => () => abortRef.current?.abort(), []);

    return { options, loading, load };
  };

  type RHFResearchAutocompleteProps = {
    name: keyof FormValues;
    label: string;
    tabella?: "ISTCOM" | "ESENZ" | "TIPRAM" | "TPRICE";
    initialCode?: string;
    initialLabel?: string;
    customLoader?: () => Promise<Option[]>;
    cacheKey?: string;
  };

  const RHFResearchAutocomplete = ({
    name,
    label,
    tabella = "ISTCOM",
    initialCode,
    initialLabel,
    customLoader,
    cacheKey,
  }: RHFResearchAutocompleteProps) => {
    const { control } = useFormContext<FormValues>();

    const initialOption = React.useMemo(
      () =>
        initialCode
          ? {
              value: String(initialCode),
              label: initialLabel
                ? `${initialCode} - ${initialLabel}`
                : String(initialCode),
            }
          : undefined,
      [initialCode, initialLabel]
    );

    const { options, loading, load } = useResearchOptions(
      tabella,
      initialOption,
      customLoader,
      cacheKey
    );

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const valueObj =
            options.find((o) => o.value === (field.value ?? "")) ?? null;
          return (
            <Autocomplete
              renderOption={(props, option) => (
                <li {...props} key={`opt_${(option as any).value}`}>
                  {(option as any).label}
                </li>
              )}
              options={options}
              value={valueObj}
              loading={loading}
              onOpen={load}
              onChange={(_, val) => field.onChange((val as any)?.value ?? "")}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.label)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  size="small"
                  fullWidth
                  placeholder="Seleziona..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          );
        }}
      />
    );
  };

  const dateRif = React.useCallback(
    () => dayjs(methods.getValues("dataVisita")).format("YYYY-MM-DD"),
    [methods]
  );

  // Ritorna il primo array trovato seguendo una lista di path noti,
  // altrimenti prova a prendere la prima proprietà che sia un array.
  const pickFirstArray = (obj: any, candidatePaths: string[]): any[] => {
    if (!obj || typeof obj !== "object") return [];
    for (const path of candidatePaths) {
      const arr = path.split(".").reduce<any>((acc, key) => acc?.[key], obj);
      if (Array.isArray(arr)) return arr;
    }
    const firstArr = Object.values(obj).find(Array.isArray);
    return Array.isArray(firstArr) ? (firstArr as any[]) : [];
  };

  // Prestazione SISS
  const loadRicAnapres = React.useCallback(async (): Promise<Option[]> => {
    const res = await ricAnapres({
      aziendaPrenotazione: azienda,
      dataRif: dateRif(),
      codSpecia: dataAppuntamento.CODSPECIA,
      codAnapre: dataAppuntamento.CODANAPRE,
      codAnapres: "",
      testValidita: "S",
      flDisattivati: "S",
    });
    const arr = res?.data?.dati ?? res?.data ?? [];
    return (Array.isArray(arr) ? arr : Object.values(arr)).map((r: any) => ({
      value: String(r.CODANAPRES ?? ""),
      label: r.DESANAPRES
        ? `${r.CODANAPRES} - ${r.DESANAPRES}`
        : String(r.CODANAPRES ?? ""),
    }));
  }, [azienda, dataAppuntamento, dateRif]);

  // Cod. Agenda
  const loadAnaggpPacada = React.useCallback(async (): Promise<Option[]> => {
    const res = await anaggpPacada({
      aziendaPrenotazione: azienda,
      dataRif: dateRif(),
      codSpecia: dataAppuntamento.CODSPECIA,
      codAnapre: dataAppuntamento.CODANAPRE,
      codAnapres:
        methods.getValues("prestazioneSISS") || dataAppuntamento.CODANAPRES,
      codMedint: methods.getValues("medico") || dataAppuntamento.CODMEDINT,
      codTpgpre: methods.getValues("tipologia") || dataAppuntamento.CODTPGPRE,
      codPgmedt: methods.getValues("programma") || dataAppuntamento.CODPGMEDT,
    });
    const arr = res?.data?.dati ?? res?.data ?? [];
    return (Array.isArray(arr) ? arr : Object.values(arr)).map((r: any) => ({
      value: String(r.CODANAGGP ?? ""),
      label: r.DESANAGGP
        ? `${r.CODANAGGP} - ${r.DESANAGGP}`
        : String(r.CODANAGGP ?? ""),
    }));
  }, [azienda, dataAppuntamento, methods, dateRif]);

  // Metodica (ricGPMetod)
  const loadRicGPMetod = React.useCallback(async (): Promise<Option[]> => {
    const res = await ricGPMetod({
      aziendaPrenotazione: azienda,
      dataRif: dateRif(),
      codAnapres:
        methods.getValues("prestazioneSISS") || dataAppuntamento.CODANAPRES,
    });

    const list = pickFirstArray(res?.data, ["dati.gpmetod", "gpmetod"]);

    return list.map((r: any) => {
      const code = String(r.CODGPMETOD ?? r.codGPMETOD ?? r.CODICE ?? "");
      const desc = r.DESGPMETOD ?? r.descrizione ?? "";
      return { value: code, label: desc ? `${code} - ${desc}` : code };
    });
  }, [azienda, dataAppuntamento, methods, dateRif]);

  //Diagnostica (ricGPQudia)
  const loadRicGPQudia = React.useCallback(async (): Promise<Option[]> => {
    const res = await ricGPQudia({
      aziendaPrenotazione: azienda,
      dataRif: dateRif(),
      codAgendaGP: methods.getValues("codAgenda") || dataAppuntamento.CODANAGGP,
      disciplinaQD: dataAppuntamento.DISCIPLINAANAGGP || "05",
    });

    // prova a estrarre l’array: può essere in data.dati.gpqudia oppure data.gpqudia
    const list = pickFirstArray(res?.data, ["dati.gpqudia", "gpqudia"]);

    return list.map((r: any) => {
      const code = String(r.CODGPQUDIA ?? r.codGPQUDIA ?? r.CODICE ?? "");
      const desc = r.DESGPQUDIA ?? r.descrizione ?? "";
      return { value: code, label: desc ? `${code} - ${desc}` : code };
    });
  }, [azienda, dataAppuntamento, methods, dateRif]);

  // Distretto (ricGPDistr)
  const loadRicGPDistr = React.useCallback(async (): Promise<Option[]> => {
    const res = await ricGPDistr({
      aziendaPrenotazione: azienda,
      dataRif: dateRif(),
      codAnapres:
        methods.getValues("prestazioneSISS") || dataAppuntamento.CODANAPRES,
    });

    // prende l’array in: data.dati.gpdistr oppure data.gpdistr
    const list = pickFirstArray(res?.data, ["dati.gpdistr", "gpdistr"]);

    return list.map((r: any, i: number) => {
      const code = String(r.CODGPDISTR ?? r.codGPDistr ?? r.CODICE ?? "");
      const desc = r.DESGPDISTR ?? r.descrizione ?? "";
      return { value: code, label: desc ? `${code} - ${desc}` : code };
    });
  }, [azienda, dataAppuntamento, methods, dateRif]);

  // Programma (ricpgmedt)
  const loadRicPgmedt = React.useCallback(async (): Promise<Option[]> => {
    const payload = {
      aziendaPrenotazione: azienda,
      codMedint: methods.getValues("medico") || dataAppuntamento.CODMEDINT,
      codSpecia: dataAppuntamento.CODSPECIA,
      codAnapre: dataAppuntamento.CODANAPRE,
      dataRif: dateRif(),
    };
    const res = await ricpgmedt(payload);
    console.log({ payload });

    const arr = res?.data?.dati?.programmi ?? res?.data?.programmi ?? [];
    return arr.map((r: any) => ({
      value: String(r.CODPGMEDT ?? ""),
      label: r.DESPGMEDT
        ? `${r.CODPGMEDT} - ${r.DESPGMEDT}`
        : String(r.CODPGMEDT ?? ""),
    }));
  }, [azienda, dataAppuntamento, methods, dateRif]);

  // Loader GENERICO per calProgPacada
  const loadIdAppuntamento = React.useCallback(async (): Promise<Option[]> => {
    const rif = dateRif();
    if (!azienda || !rif) return [];

    const res = await calProgPacada({
      aziendaPrenotazione: azienda,
      dataRif: rif,
      tipoProg: "A", // "A" per Appuntamento
      flagConsideraTermin: "0",
    });

    const prog = res?.data?.dati?.progressivo ?? res?.data?.progressivo ?? "";

    return prog ? [{ value: String(prog), label: String(prog) }] : [];
  }, [azienda, dateRif]);

  const loadIdCombinato = React.useCallback(async (): Promise<Option[]> => {
    const rif = dateRif();
    if (!azienda || !rif) return [];

    const res = await calProgPacada({
      aziendaPrenotazione: azienda,
      dataRif: rif,
      tipoProg: "C", // "C" per Combinato
      flagConsideraTermin: "0",
    });

    const prog = res?.data?.dati?.progressivo ?? res?.data?.progressivo ?? "";

    return prog ? [{ value: String(prog), label: String(prog) }] : [];
  }, [azienda, dateRif]);

  // Quando certi campi cambiano, azzeriamo i campi dipendenti per evitare incoerenze
  // Uso watch() per seguire le modifiche del form e pulire i valori collegati
  React.useEffect(() => {
    // Avvia la sottoscrizione alle modifiche del form.
    const sub = methods.watch((values, { name }) => {
      // Se cambia una di queste chiavi padre
      if (
        name === "prestazioneSISS" ||
        name === "medico" ||
        name === "programma"
      ) {
        methods.setValue("codAgenda", "");
        methods.setValue("metodica", "");
        methods.setValue("distretto", "");
      }

      // Se cambia l’agenda, il distretto dipende da essa e va resettato
      if (name === "codAgenda") {
        methods.setValue("distretto", "");
      }
    });

    // Annulla la sottoscrizione quando il componente si smonta
    // o quando cambia methods, per evitare memory leak.
    return () => sub.unsubscribe?.();
  }, [methods]);

  return (
    // LocalizationProvider serve ai DatePicker per capire come leggere/scrivere le date nel formato richiesto
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Helmet>
        <title>Dettaglio Appuntamento</title>
      </Helmet>
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={onAlertConfirm}
      />
      <FormProvider {...methods}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StampaAgendaModale
            methods={methods}
            showModaleStampaAgenda={showModaleStampaAgenda}
            setShowModaleStampaAgenda={setShowModaleStampaAgenda}
          />
        </LocalizationProvider>
        <Box sx={{ p: 2 }}>
          <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, mx: "auto" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab label="Dettaglio" />
              <Tab label="Dati GP" />
            </Tabs>

            {/* FORM */}
            <Box component="form">
              {/* Tab Dettaglio */}
              <TabPanel value={tab} index={0}>
                <Grid container spacing={2} alignItems={"center"}>
                  <Grid item xs={12} paddingBottom={1}>
                    <Typography
                      variant="h6"
                      fontSize={14}
                      fontFamily={"Rubik"}
                      color={"rgba(0, 0, 0, 0.60)"}
                      fontWeight={500}
                    >
                      Dettaglio appuntamenti per prestazione
                    </Typography>
                  </Grid>
                  {/* Prima riga */}
                  <Grid item xs={12} md={6}>
                    <RHFDate
                      label="Data Prenotazione"
                      name="dataPrenotazione"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText
                      name="numeroPrenotazione"
                      label="N° Prenotazione"
                    />
                  </Grid>

                  {/* Seconda riga */}
                  <Grid item xs={12} md={6}>
                    <RHFText name="paziente" label="Paziente" disabled />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFAutocomplete
                      name="medico"
                      label="Medico"
                      options={mediciOpts}
                    />
                  </Grid>

                  {/* Terza riga */}
                  <Grid item xs={12} md={5}>
                    <RHFDate label="Data Visita" name="dataVisita" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <RHFTime label="Ora Visita" name="oraVisita" />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ display: "flex" }}>
                    <RHFCheck name="forzatura" label="Forzatura" />
                    <RHFCheck name="usufruito" label="Usufruito" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <RHFText name="durata" label="Durata" />
                  </Grid>
                  <Grid item xs={12} md={5}></Grid>
                  <Grid item xs={12} md={2} sx={{ display: "flex", gap: 1 }}>
                    <RHFCheck name="inArrivo" label="In Arrivo" />
                    <RHFCheck name="arrivato" label="Arrivato" />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <RHFText name="operatore" label="Operatore" />
                  </Grid>
                  <Grid item xs={12} md={4}></Grid>
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{ display: "flex", gap: 2, flexDirection: "row" }}
                  >
                    <RHFCheck name="assenza" label="Assenza" />
                  </Grid>

                  {/* Quarta riga */}
                  <Grid item xs={12} md={6}>
                    <RHFDate
                      label="Data Accettazione"
                      name="dataAccettazione"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFText
                      name="numeroAccettazione"
                      label="N° Accettazione"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={resetAccettazione}
                    >
                      Reset Accettazione
                    </Button>
                  </Grid>

                  {/* Quinta riga */}
                  <Grid item xs={12} md={3}>
                    <RHFDate label="Data Arrivo" name="dataArrivo" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <RHFTime label="Ora Arrivo" name="oraArrivo" />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                    display={"flex"}
                    justifyContent={"space-evenly"}
                  >
                    <RHFCheck
                      name="tolleranzaGiorniAttesa"
                      label="Tolleranza giorni attesa"
                    />
                    <RHFCheck name="approfondimento" label="Approfondimento" />
                    <RHFCheck name="suggerita" label="Suggerita" />
                  </Grid>

                  {/* Sesta riga */}
                  <Grid item xs={12} md={5}>
                    <RHFDate
                      name="primaDisponibilita"
                      label="Prima disponibilità"
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <RHFText name="idPAI" label="ID PAI" />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={2}
                    display={"flex"}
                    justifyContent={"center"}
                  >
                    <RHFCheck name="cronico" label="Cronico" />
                  </Grid>

                  {/* Settima riga */}
                  <Grid item xs={12} md={3}>
                    <RHFText name="competenza" label="Competenza" />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <RHFText name="protPIC" label="Prot. PIC" />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFText name="tipologia" label="Tipologia" />
                  </Grid>

                  <Grid item xs={12} md={2} sx={{ display: "flex" }}>
                    <RHFCheck name="primoAccesso" label="Primo accesso" />
                  </Grid>

                  {/* Ottava Riga: Impegnativa */}
                  <Grid item xs={12} md={5}>
                    <RHFText name="impegnativa" label="Impegnativa" />
                  </Grid>
                  <Grid item xs={12} md={1} alignItems="center">
                    <Button
                      startIcon={
                        <img
                          src={lombardiaIcon}
                          alt="Regione Lombardia"
                          style={{ width: 40, height: 40 }}
                        />
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFDate name="dataImpegnativa" label="Data Impegnativa" />
                  </Grid>

                  {/* Nona Riga */}
                  <Grid item xs={12} md={6}>
                    <RHFText name="iup" label="IUP" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText name="classePriorita" label="Classe Priorità" />
                  </Grid>

                  {/* Decima Riga */}
                  <Grid item xs={12}>
                    <RHFText
                      name="diagnosi"
                      label="Diagnosi"
                      multiline
                      minRows={3}
                      helperText={
                        (methods.watch("diagnosi")?.length ?? 0) + "/400"
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <RHFText
                      name="notePrestazione"
                      label="Note Prestazione"
                      helperText={
                        (methods.watch("notePrestazione")?.length ?? 0) + "/400"
                      }
                      multiline
                      minRows={3}
                    />
                  </Grid>

                  {/* Undicesima Riga */}
                  <Grid item xs={12} md={4}>
                    <RHFText
                      name="idTipoPrestazione"
                      label="ID. tipo prestazione"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFAutocomplete
                      name="medicoRelaz"
                      label="Med. Relaz"
                      options={mediciOpts}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box flex={1}>
                        <RHFAutocomplete
                          name="medicoSostituto"
                          label="Med. Sostituto"
                          options={mediciOpts}
                        />
                      </Box>
                      <RHFCheck name="temporaneo" label="Temporaneo" disabled />
                    </Stack>
                  </Grid>
                </Grid>
              </TabPanel>
              {/* TAB 1: DATI GP */}
              <TabPanel value={tab} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="idAppuntamento"
                      label="ID appuntamento"
                      initialCode={String(dataAppuntamento?.IDAPPGP || "")}
                      customLoader={loadIdAppuntamento}
                      cacheKey={`progA|${azienda}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="idCombinato"
                      label="ID Combinato"
                      initialCode={String(
                        dataAppuntamento?.IDPROGCOMBINATO || ""
                      )}
                      customLoader={loadIdCombinato}
                      cacheKey={`progC|${azienda}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText name="codiceIURP" label="Codice IURP" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="prestazioneSISS"
                      label="Prestazione SISS"
                      initialCode={String(dataAppuntamento?.CODANAPRES || "")}
                      initialLabel={dataAppuntamento?.DESANAPRES}
                      customLoader={loadRicAnapres}
                      cacheKey={`anapres|${azienda}|${
                        dataAppuntamento?.CODSPECIA
                      }|${dataAppuntamento?.CODANAPRE}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="codAgenda"
                      label="Cod. agenda"
                      initialCode={String(dataAppuntamento?.CODANAGGP || "")}
                      initialLabel={dataAppuntamento?.DESANAGGP}
                      customLoader={loadAnaggpPacada}
                      cacheKey={`anaggp|${azienda}|${
                        dataAppuntamento?.CODSPECIA
                      }|${dataAppuntamento?.CODANAPRE}|${methods.getValues(
                        "prestazioneSISS"
                      )}|${methods.getValues("medico")}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="idCombinata"
                      label="ID combinata"
                      tabella="ISTCOM"
                      initialCode={String(dataAppuntamento?.CODISTCOM || "")}
                      initialLabel={dataAppuntamento?.DESISTCOM}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="quadroDiagnostico"
                      label="Quesito diagnostico"
                      initialCode={String(dataAppuntamento?.CODGPQUDIA || "")}
                      initialLabel={dataAppuntamento?.DESGPQUDIA}
                      customLoader={loadRicGPQudia}
                      cacheKey={`gpqudia|${azienda}|${methods.getValues(
                        "codAgenda"
                      )}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="metodica"
                      label="Metodica"
                      initialCode={String(dataAppuntamento?.CODGPMETOD || "")}
                      initialLabel={dataAppuntamento?.DESGPMETOD}
                      customLoader={loadRicGPMetod}
                      cacheKey={`gpmetod|${azienda}|${
                        dataAppuntamento?.CODSPECIA
                      }|${dataAppuntamento?.CODANAPRE}|${methods.getValues(
                        "prestazioneSISS"
                      )}|${methods.getValues("programma")}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="distretto"
                      label="Distretto"
                      initialCode={String(dataAppuntamento?.CODGPDISTR || "")}
                      initialLabel={dataAppuntamento?.DESGPDISTR}
                      customLoader={loadRicGPDistr}
                      cacheKey={`gpdistr|${azienda}|${
                        dataAppuntamento?.CODSPECIA
                      }|${dataAppuntamento?.CODANAPRE}|${methods.getValues(
                        "prestazioneSISS"
                      )}|${methods.getValues("programma")}|${methods.getValues(
                        "codAgenda"
                      )}|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="esenzione"
                      label="Esenzione"
                      tabella="ESENZ"
                      initialCode={String(dataAppuntamento?.CODESENZ || "")}
                      initialLabel={dataAppuntamento?.DESESENZ}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText name="prescrittore" label="Prescrittore" />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="provenienza"
                      label="Provenienza"
                      tabella="TIPRAM"
                      initialCode={String(dataAppuntamento?.CODTIPRAM || "")}
                      initialLabel={dataAppuntamento?.DESTIPRAM}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="tipoRicetta"
                      label="Tipo ricetta"
                      tabella="TPRICE"
                      initialCode={String(dataAppuntamento?.CODTPRICE || "")}
                      initialLabel={dataAppuntamento?.DESTPRICE}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFResearchAutocomplete
                      name="programma"
                      label="Programma"
                      initialCode={String(dataAppuntamento?.CODPGMEDT || "")}
                      initialLabel={dataAppuntamento?.DESPGMEDT}
                      customLoader={loadRicPgmedt}
                      cacheKey={`pgmedt|${azienda}|${methods.getValues(
                        "medico"
                      )}|${dataAppuntamento?.CODSPECIA}|${
                        dataAppuntamento?.CODANAPRE
                      }|${dateRif()}`}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText name="nomenclatoreReg" label="Nomenclatore Reg." />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RHFText name="catalogoPrescr" label="Catalogo prescr." />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Footer con i button di Conferma e Uscita */}
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} md={6}>
                  <Button
                    variant="outlinedError"
                    color="error"
                    onClick={() => {
                      onClose();
                      prenotazioneRequest(
                        dataAppuntamento?.NUMPRENOTAZIONE,
                        azienda ?? ""
                      );
                    }}
                  >
                    Uscita
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined">Acc. Manuale</Button>
                    <Button variant="outlined">Log GP</Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowModaleStampaAgenda(true)}
                    >
                      St.Agenda
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={askConfirmEditPrenotazione}
                    >
                      Conferma
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Box>
      </FormProvider>
    </LocalizationProvider>
  );
}
